import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Button, Checkbox, Tooltip } from '@mui/material';
import { Box } from '@mui/system';
import { DataGridPro, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { UserDTO } from '../../api/generated';
import { useUpdateUserMutation, useDeleteUserMutation } from '../../api/mutations/user';
import { useJobsiteList } from '../../api/queries/jobsite';
import { useUserList } from '../../api/queries/user';
import { useUserContext } from '../../UserContext';
import { getDataGridLocaleText, getUserPathId } from '../../utils';
import { DefaultErrorScreen, DefaultLoadingScreen } from '../ApiLoadingGuard';

export function ManageUsers() {
  const { t } = useTranslation();
  const { data: user } = useUserContext();
  const { mutate: updateUser } = useUpdateUserMutation();
  const { mutate: deleteUser } = useDeleteUserMutation();
  const apiRef = useGridApiRef();

  const {
    data: usersData,
    isPending: isUsersPending,
    isStale: isUsersStale,
    isError: isUsersError,
  } = useUserList({ organisationId: user?.organisation.id });
  const {
    data: jobsitesData,
    isPending: isJobsitesPending,
    isStale: isJobsitesStale,
    isError: isJobsitesError,
  } = useJobsiteList({ organisationId: user?.organisation.id });

  const isLoading = isUsersPending || isUsersStale || isJobsitesPending || isJobsitesStale;
  const isError = isUsersError || isJobsitesError;

  if (isError) {
    return <DefaultErrorScreen />;
  }

  if (isLoading) {
    return <DefaultLoadingScreen />;
  }

  const users = usersData.data;
  const jobsites = jobsitesData.data;

  function handleCheckboxJobsiteChange(jobsiteId: string) {
    return async (event: ChangeEvent<HTMLInputElement>) => {
      const userId = event.currentTarget.name;
      const affectedUser = users.find((u) => u.id === userId);
      const isCurrentlyChecked = event.target.checked;

      let userJobsiteIdList = affectedUser?.jobsiteIds?.map((j) => j) || [];
      if (isCurrentlyChecked) {
        // push jobsiteId to user jobsites
        userJobsiteIdList?.push(jobsiteId);
      } else {
        // remove jobsiteId from user jobsites
        userJobsiteIdList = userJobsiteIdList?.filter((j) => j !== jobsiteId);
      }

      const pathId = getUserPathId(affectedUser);
      if (!pathId) return;

      updateUser({
        authId: pathId,
        data: {
          jobsiteIds: userJobsiteIdList,
        },
      });
    };
  }

  async function handleCheckboxAdminChange(event: ChangeEvent<HTMLInputElement>) {
    const userId = event.currentTarget.name;
    const affectedUser = users?.find((u) => u.id === userId);
    const isAdmin = event.target.checked;

    const pathId = getUserPathId(affectedUser);
    if (!pathId) return;

    updateUser({
      authId: pathId,
      data: {
        admin: isAdmin,
      },
    });
  }

  function handleRemoveUsers() {
    const selectedRows = apiRef.current?.getSelectedRows();
    selectedRows?.forEach((row) => {
      const pathId = getUserPathId(row as UserDTO);
      if (!pathId) return;

      deleteUser({
        authId: pathId,
      });
    });
  }

  return (
    <Box display='flex' height='50vh' flexDirection='column'>
      <DataGridPro
        localeText={getDataGridLocaleText()}
        rows={
          users.map((user: UserDTO) => ({
            ...user,
            ...Object.fromEntries(user.jobsiteIds.map((j) => [`jobsite_${j}`, true])),
          })) || []
        }
        columns={[
          {
            field: 'displayName',
            headerName: t('common.form.name'),
            minWidth: 180,
            renderCell: (params: GridRenderCellParams) => (
              <Box display='flex' alignItems='center' justifyContent='flex-start'>
                {!params.row.activated && (
                  <Tooltip title={t('administration.userPermissions.registrationPending')} arrow>
                    <ErrorOutlineIcon style={{ color: 'crimson', marginRight: '1rem' }} />
                  </Tooltip>
                )}
                {params.value}
              </Box>
            ),
          },
          {
            field: 'email',
            headerName: t('common.form.email'),
            minWidth: 200,
          },
          {
            field: 'admin',
            headerName: t('administration.userPermissions.administrator'),
            headerAlign: 'center',
            minWidth: 120,
            align: 'center',
            renderCell: (params: GridRenderCellParams) => (
              <strong>
                <Checkbox
                  name={params.id.toString()}
                  checked={params.value || false}
                  onChange={handleCheckboxAdminChange}
                />
              </strong>
            ),
          } as any, // cast to any, otherwise MUI throws an error with the align property
        ].concat(
          jobsites.map((j) => {
            return {
              field: `jobsite_${j.id}`,
              headerName: j.name,
              headerAlign: 'center',
              align: 'center',
              minWidth: 250,
              renderCell: (params: GridRenderCellParams) => (
                <strong>
                  <Checkbox
                    name={params.id.toString()}
                    checked={params.value || false}
                    onChange={handleCheckboxJobsiteChange(j.id)}
                  />
                </strong>
              ),
            };
          }) || []
        )}
        checkboxSelection
        disableRowSelectionOnClick
        disableMultipleRowSelection={true}
        apiRef={apiRef}
        hideFooter={true}
        loading={isLoading}
      />
      <Box sx={{ display: 'flex' }}>
        <Button sx={{ marginTop: 2 }} variant='contained' onClick={handleRemoveUsers}>
          {t('administration.userPermissions.deleteUser')}
        </Button>
      </Box>
    </Box>
  );
}
